import { AxiosInstance, AxiosResponse } from "axios"
import { GenericResponse } from "."

export interface CustomerModel {
    id: number
    fname: string
    lname: string
    phone: string
    country: string
    code: string
    email: string
    postalCode?: string
    user_pin: string
    user_type: string
    name: string
    state: string
    city: string
    address: string
    gender: string
    created_by: CustomerModel
    updated_by: CustomerModel
}

export interface CustomerListModel {
    total: number
    customers: []
    page: number
    page_size: number
}

export interface CreateCustomerRequest {
    fname: string
    lname: string
    phone: string
    country: string
    code: string
    email: string
    postalCode?: string
    user_pin: string
    user_type: string
    name: string
    state: string
    city: string
    address: string
    gender: string
}

export interface UpdateCustomerRequest {
    id?: number
    fname?: string
    lname?: string
    phone?: string
    country?: string
    code?: string
    email?: string
    postalCode?: string
    user_pin?: string
    user_type?: string
    name?: string
    state?: string
    city?: string
    address?: string
    gender?: string
}

export interface DowngradeCustomerRequest {
    id: number
    target: string
}

export interface UpgradeCustomerRequest {
    id: number
    target: string
    promocode: string
}

export interface ApplyPromoCodeRequest {
    id: number
    promocode: string
}

export interface CreditCustomerRequest {
    id: number
    mark: number | boolean
}

export interface UpdateOrCreateAddressRequest {
    id?: number
    addressId?: number
    fname?: string
    lname?: string
    phone?: string
    country?: string
    code?: string
    city_id?: number
    city?: string
    address?: string
    address2?: string
    is_primary?: number
    user_id?: number
    created_at?: string
}

export interface paginationData {
    page?: number
    pageSize?: number
    warehouse?: string
    customerSearch?: {
        plan_type: string
        customer: string
        country: string
        email_verification: string
        date: string
    }
}
const customerApi = (apiInstance: AxiosInstance) => {
    const customersList = async ({ warehouse }: { warehouse?: string }): Promise<GenericResponse> => {
        let warehouseValue = warehouse || 1
        const res: Promise<GenericResponse> = apiInstance.get(
            `/customers/all-customers?warehouse=${warehouseValue}`
        )
        return res
    }

    const paginatedCustomersList = async (data: paginationData): Promise<GenericResponse> => {
        var params = data.page ? "page=" + data.page : "page=1"
        params += data.warehouse ? "&warehouse=" + data.warehouse : 1
        params += data.pageSize ? "&page_size=" + data.pageSize : ""
        params += data.customerSearch?.plan_type ? "&plan_type=" + data.customerSearch.plan_type : ""
        params += data.customerSearch?.customer ? "&customer=" + data.customerSearch.customer : ""
        params += data.customerSearch?.country ? "&destination=" + data.customerSearch.country : ""
        params += data.customerSearch?.email_verification
            ? "&email_verification=" + data.customerSearch.email_verification
            : ""
        params += data.customerSearch?.date ? "&date_range=" + data.customerSearch.date : ""
        var url = "/v2/customers/all-customers"
        url = params ? url + "?" + params : ""
        const res: Promise<GenericResponse> = apiInstance.get(url)
        return res
    }

    const createCustomer = async (newCustomerData: CreateCustomerRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/create-customer`, newCustomerData)
        return res
    }

    const updateCustomer = async (existingCustomerData: UpdateCustomerRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.put(`/customers/${existingCustomerData?.id}`, {
            ...existingCustomerData,
            user_id: existingCustomerData?.id,
            code: existingCustomerData?.country
        })
        return res
    }

    const verifyCustomer = async (customerId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/verify-manual`, { id: customerId })
        return res
    }

    const downgradeCustomer = async (
        downgradeCustomerData: DowngradeCustomerRequest
    ): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/downgrade`, downgradeCustomerData)
        return res
    }

    const upgradeCustomer = async (upgradeCustomerData: UpgradeCustomerRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/upgrade`, upgradeCustomerData)
        return res
    }

    const applyPromoCode = async (applyPromoCodeData: ApplyPromoCodeRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(
            `/customers/apply-promo-code`,
            applyPromoCodeData
        )
        return res
    }

    const creditCustomer = async (creditCustomerData: CreditCustomerRequest): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/credit-mark`, creditCustomerData)
        return res
    }

    const loadCountry = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/location/country`)
        return res
    }

    const loadCities = async (countryCode: string): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/location/states/${countryCode}`)
        return res
    }

    const updateOrCreateCustomerAddress = async (
        updatedOrCreateAddressData: UpdateOrCreateAddressRequest
    ): Promise<GenericResponse> => {
        const { addressId = null } = updatedOrCreateAddressData

        delete updatedOrCreateAddressData["id"]
        delete updatedOrCreateAddressData["created_at"]
        delete updatedOrCreateAddressData["is_primary"]
        let res: Promise<GenericResponse>

        if (addressId) {
            delete updatedOrCreateAddressData["addressId"]
            res = apiInstance.post(`account/address/${addressId}`, updatedOrCreateAddressData)
        } else {
            res = apiInstance.post(`account/address`, updatedOrCreateAddressData)
        }

        return res
    }

    const listFullFreePromoCodes = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/customers/all-full-free-promocodes`)
        return res
    }

    const deleteCustomer = async (customerId: number): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.post(`/customers/delete`, { id: customerId })
        return res
    }

    const creditCustomersList = async ({ warehouse }: { warehouse?: string }): Promise<GenericResponse> => {
        let warehouseValue = warehouse || 1
        const res: Promise<GenericResponse> = apiInstance.get(`/customers/credit?warehouse=${warehouseValue}`)
        return res
    }

    const warehousesList = async (): Promise<GenericResponse> => {
        const res: Promise<GenericResponse> = apiInstance.get(`/location/warehouses`)
        return res
    }

    const customerStats = async (customerPin: string): Promise<GenericResponse> => {
        return apiInstance.get(`/customers/stats/${customerPin}`)
    }

    const loadCustomersDestinations = async (): Promise<GenericResponse> => {
        return apiInstance.get(`/customers/all-countries`)
    }

    // get wallet amount
    const walletAmount = async (userId: number): Promise<AxiosResponse> => {
        return await apiInstance.get(`/account/wallet-amount?user_id=${userId}`)
    }

    const updateWalletAmount = async (userId: number, walletAmount: number): Promise<AxiosResponse> => {
        return await apiInstance.post(`/account/wallet-update?user_id=${userId}&amount=${walletAmount}`)
    }

    return {
        customersList,
        createCustomer,
        updateCustomer,
        verifyCustomer,
        downgradeCustomer,
        creditCustomer,
        loadCountry,
        updateOrCreateCustomerAddress,
        loadCities,
        listFullFreePromoCodes,
        upgradeCustomer,
        applyPromoCode,
        deleteCustomer,
        creditCustomersList,
        warehousesList,
        customerStats,
        paginatedCustomersList,
        loadCustomersDestinations,
        walletAmount,
        updateWalletAmount
    }
}

export default customerApi
